import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  type Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CurrentBrandScope } from '@principle-theorem/ng-principle-shared';
import {
  DepositRequestDeliveryChannel,
  DepositStatus,
  type IDepositMetadata,
} from '@principle-theorem/principle-core/interfaces';
import { type IWaiveDepositDialogResult } from '../../appointment-view-sidebar/appointment-deposit-sidebar/waive-deposit-dialog/waive-deposit-dialog.component';
import { WaiveDepositDialogService } from '../../appointment-view-sidebar/appointment-deposit-sidebar/waive-deposit-dialog/waive-deposit-dialog.service';
import { type IDepositAbsence } from '../deposit-status-chip/deposit-chip-display';
import { DepositStatusChipComponent } from '../deposit-status-chip/deposit-status-chip.component';

const outstandingStatuses: readonly DepositStatus[] = [
  DepositStatus.Required,
  DepositStatus.Pending,
];

@Component({
  selector: 'pr-deposit-actions',
  templateUrl: './deposit-actions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DepositStatusChipComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  providers: [WaiveDepositDialogService],
})
export class DepositActionsComponent {
  private readonly _waiveDepositDialog = inject(WaiveDepositDialogService);
  readonly depositsEnabled = toSignal(
    inject(CurrentBrandScope).depositsEnabled$,
    {
      initialValue: false,
    }
  );

  readonly deposit = input<IDepositMetadata>();
  readonly absence = input<IDepositAbsence>();
  readonly sendingChannel = input<DepositRequestDeliveryChannel>();
  /** Must be derived from the `appointment.deposit.override` permission. */
  readonly canOverrideDeposit = input(false, {
    transform: coerceBooleanProperty,
  });
  readonly paymentLinkAvailable = input(false, {
    transform: coerceBooleanProperty,
  });
  /**
   * Only a booking request can supersede its cancelled request with a fresh
   * one.
   */
  readonly resendAfterCancelAvailable = input(false, {
    transform: coerceBooleanProperty,
  });
  /** Reinstating a waived deposit needs a saved appointment. */
  readonly reinstateAvailable = input(false, {
    transform: coerceBooleanProperty,
  });
  readonly hideChip = input(false, { transform: coerceBooleanProperty });
  readonly labelledActions = input(false, { transform: coerceBooleanProperty });
  readonly takeDepositAvailable = input(false, {
    transform: coerceBooleanProperty,
  });

  readonly sendPaymentLink = output<DepositRequestDeliveryChannel>();
  readonly waiveDeposit = output<IWaiveDepositDialogResult>();
  readonly unwaiveDeposit = output<void>();
  readonly takeDeposit = output<void>();

  readonly DepositRequestDeliveryChannel = DepositRequestDeliveryChannel;

  readonly sending: Signal<boolean> = computed(
    () => this.sendingChannel() !== undefined
  );

  readonly collectable: Signal<boolean> = computed(() => {
    const deposit = this.deposit();
    if (!deposit) {
      return false;
    }
    if (outstandingStatuses.includes(deposit.status)) {
      return true;
    }
    return (
      deposit.status === DepositStatus.Cancelled &&
      this.resendAfterCancelAvailable()
    );
  });

  readonly canSend: Signal<boolean> = computed(
    () => this.paymentLinkAvailable() && this.collectable()
  );

  readonly canWaive: Signal<boolean> = computed(() => {
    if (!this.canOverrideDeposit()) {
      return false;
    }
    return this.deposit() ? this.collectable() : true;
  });

  readonly canReinstate: Signal<boolean> = computed(
    () =>
      this.canOverrideDeposit() &&
      this.reinstateAvailable() &&
      this.deposit()?.status === DepositStatus.Waived
  );

  readonly canTakeDeposit: Signal<boolean> = computed(() => {
    const deposit = this.deposit();
    return (
      this.takeDepositAvailable() &&
      deposit !== undefined &&
      outstandingStatuses.includes(deposit.status)
    );
  });

  readonly hasActions: Signal<boolean> = computed(
    () =>
      this.depositsEnabled() &&
      (this.canSend() ||
        this.canTakeDeposit() ||
        this.canWaive() ||
        this.canReinstate())
  );

  send(deliveryChannel: DepositRequestDeliveryChannel): void {
    if (!this.canSend() || this.sending()) {
      return;
    }
    this.sendPaymentLink.emit(deliveryChannel);
  }

  async waive(): Promise<void> {
    if (!this.canWaive() || this.sending()) {
      return;
    }
    const result = await this._waiveDepositDialog.open();
    if (result) {
      this.waiveDeposit.emit(result);
    }
  }

  unwaive(): void {
    if (!this.canReinstate()) {
      return;
    }
    this.unwaiveDeposit.emit();
  }
}
